import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ChartBar, Broadcast, WarningCircle, MoonStars, Export } from "@phosphor-icons/react";
import { ExportDialog } from "../components/ExportDialog";
import { btnGhost } from "../components/checklists/shared";
import { api, apiError } from "../lib/api";
import { useRealtime } from "../lib/realtime";
import { useDepartments } from "../components/DepartmentPicker";
import { inputCls, fmtTime, fmtDay } from "../components/checklists/shared";

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };

function Kpi({ label, value, tone = "text-slate-900", testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl px-4 py-3">
      <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">{label}</p>
      <p className={`font-heading text-2xl font-bold mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}

export default function CompliancePage() {
  const nav = useNavigate();
  const departments = useDepartments();
  const [hier, setHier] = useState(null);
  const [config, setConfig] = useState(null);
  const [f, setF] = useState({ mode: "today", preset: "today", date_from: daysAgo(6), date_to: iso(new Date()), cluster_id: "", region_id: "", venue_id: "", department_id: "", shift: "" });
  const [data, setData] = useState(null);
  const timer = useRef(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/hierarchy").then((r) => setHier(r.data)).catch(() => {});
    api.get("/checklists/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const rangeError = f.mode === "range" && f.date_from && f.date_to
    ? (f.date_to < f.date_from ? "End before start" : (new Date(f.date_to) - new Date(f.date_from)) / 86400000 > 92 ? "Max 92 days" : "")
    : "";
  const load = useCallback(() => {
    if (rangeError) return;
    const params = { ...f };
    delete params.preset;
    api.get("/compliance/outlets", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [f, rangeError]);
  useEffect(() => { load(); }, [load]);
  const live = useRealtime((m) => {
    if (["checklist.run", "shift.state", "checklist.template", "hierarchy.changed"].includes(m.type) && f.mode === "today") {
      clearTimeout(timer.current); timer.current = setTimeout(load, 400);
    }
  });

  const setPreset = (p) => {
    if (p === "today") return setF({ ...f, mode: "today", preset: p });
    if (p === "7d") return setF({ ...f, mode: "range", preset: p, date_from: daysAgo(6), date_to: iso(new Date()) });
    if (p === "30d") return setF({ ...f, mode: "range", preset: p, date_from: daysAgo(29), date_to: iso(new Date()) });
    setF({ ...f, mode: "range", preset: "custom" });
  };
  const tiers = hier?.tiers || [];
  const twoTier = tiers.length === 2;
  const top = (hier?.nodes || []).filter((n) => n.tier === 0);
  const leaves = (hier?.nodes || []).filter((n) => n.tier === tiers.length - 1 && (!f.cluster_id || !twoTier || n.parent_id === f.cluster_id));
  const venues = (hier?.venues || []).filter((v) => (!f.region_id || v.node_id === f.region_id) && (!f.cluster_id || !twoTier || v.chain.includes(f.cluster_id)));
  const rows = data?.venues || [];
  const t = data?.totals;
  const pctTone = (p) => (p >= 85 ? "text-emerald-700" : p >= 60 ? "text-amber-700" : "text-red-700");
  const barTone = (p) => (p >= 85 ? "bg-emerald-500" : p >= 60 ? "bg-amber-500" : "bg-red-500");

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400 flex items-center gap-2">Outlet compliance
            <span data-testid="compliance-live" className={`inline-flex items-center gap-1 text-[10px] tracking-normal px-2 py-0.5 rounded border whitespace-nowrap ${live === "live" && f.mode === "today" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-400 bg-slate-50 border-slate-200"}`}>
              <Broadcast size={12} weight={live === "live" ? "fill" : "regular"} /> {f.mode === "today" ? (live === "live" ? "Live" : "Reconnecting") : "Historical"}
            </span>
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2 flex items-center gap-3"><ChartBar size={32} weight="duotone" className="text-sky-600" /> Compliance</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <button data-testid="compliance-export" onClick={() => setExporting(true)} className={`${btnGhost} flex items-center gap-1.5`}><Export size={16} /> Export logs</button>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1" data-testid="compliance-period">
          {[["today", "Today (live)"], ["7d", "7 days"], ["30d", "30 days"], ["custom", "Custom"]].map(([k, l]) => (
            <button key={k} data-testid={`period-${k}`} onClick={() => setPreset(k)} className={`h-9 px-3 rounded-md text-xs font-bold ${f.preset === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
          {f.preset === "custom" && (
            <>
              <input data-testid="period-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-9 px-2 text-xs border border-slate-200 rounded-md" />
              <input data-testid="period-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-9 px-2 text-xs border border-slate-200 rounded-md" />
              {rangeError && <span data-testid="period-range-error" className="text-[11px] font-bold text-red-600 px-1">{rangeError}</span>}
            </>
          )}
        </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2" data-testid="compliance-filters">
        {twoTier && (
          <select data-testid="filter-cluster" value={f.cluster_id} onChange={(e) => setF({ ...f, cluster_id: e.target.value, region_id: "", venue_id: "" })} className={inputCls}>
            <option value="">All {tiers[0]?.label}s</option>{top.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
          </select>
        )}
        <select data-testid="filter-region" value={f.region_id} onChange={(e) => setF({ ...f, region_id: e.target.value, venue_id: "" })} className={inputCls}>
          <option value="">All {tiers[tiers.length - 1]?.label}s</option>{leaves.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
        </select>
        <select data-testid="filter-venue" value={f.venue_id} onChange={(e) => setF({ ...f, venue_id: e.target.value })} className={inputCls}>
          <option value="">All venues</option>{venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select data-testid="filter-department" value={f.department_id} onChange={(e) => setF({ ...f, department_id: e.target.value })} className={inputCls}>
          <option value="">All departments</option>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select data-testid="filter-shift" value={f.shift} onChange={(e) => setF({ ...f, shift: e.target.value })} className={inputCls}>
          <option value="">All shifts</option>{(config?.shifts || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2" data-testid="compliance-kpis">
          <Kpi label="Outlets" value={t.venues} testId="kpi-outlets" />
          <Kpi label="Completion" value={`${t.completion_pct}%`} tone={pctTone(t.completion_pct)} testId="kpi-completion" />
          <Kpi label="Awaiting verification" value={t.awaiting} tone={t.awaiting ? "text-amber-700" : "text-slate-900"} testId="kpi-awaiting" />
          <Kpi label="Open exceptions" value={t.exceptions} tone={t.exceptions ? "text-red-700" : "text-slate-900"} testId="kpi-exceptions" />
          <Kpi label="Critical" value={t.critical_exceptions} tone={t.critical_exceptions ? "text-red-700" : "text-slate-900"} testId="kpi-critical" />
          <Kpi label="Stale operating days" value={t.stale_venues} tone={t.stale_venues ? "text-red-700" : "text-slate-900"} testId="kpi-stale" />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto" data-testid="compliance-table">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Venue</th>
              {tiers.slice().reverse().map((tt) => <th key={tt.key} className="text-left px-3 py-3">{tt.label}</th>)}
              <th className="text-left px-3 py-3 w-48">Completion</th>
              <th className="text-right px-3 py-3">Checklists</th>
              <th className="text-right px-3 py-3">Not started</th>
              <th className="text-right px-3 py-3">Awaiting</th>
              <th className="text-right px-3 py-3">Verified</th>
              <th className="text-right px-3 py-3">Exceptions</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-left px-3 py-3">Last activity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.venue_id} data-testid={`compliance-row-${r.venue_id}`} onClick={() => nav(`/checklists?venue_id=${r.venue_id}`)} className="hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3 font-bold text-slate-900">{r.venue}<span className="block text-[11px] font-semibold text-slate-400">{r.local_time} local</span></td>
                <td className="px-3 py-3 text-slate-600">{r.region || "—"}</td>
                {twoTier && <td className="px-3 py-3 text-slate-600">{r.cluster || "—"}</td>}
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${barTone(r.completion_pct)}`} style={{ width: `${r.completion_pct}%` }} /></div>
                    <span className={`text-xs font-bold w-10 text-right ${pctTone(r.completion_pct)}`} data-testid={`row-pct-${r.venue_id}`}>{r.completion_pct}%</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">{r.lines_done}/{r.lines_total} lines</p>
                </td>
                <td className="px-3 py-3 text-right font-semibold">{r.runs}</td>
                <td className="px-3 py-3 text-right text-slate-500">{r.not_started}</td>
                <td className={`px-3 py-3 text-right font-bold ${r.awaiting ? "text-amber-700" : "text-slate-400"}`}>{r.awaiting}</td>
                <td className="px-3 py-3 text-right text-emerald-700 font-bold">{r.verified}</td>
                <td className={`px-3 py-3 text-right font-bold ${r.exceptions ? "text-red-700" : "text-slate-400"}`}>{r.exceptions}{r.critical_exceptions ? <span className="text-[10px] ml-1">({r.critical_exceptions} crit)</span> : null}</td>
                <td className="px-3 py-3">
                  {r.stale ? <span className="text-[11px] font-bold text-red-700 flex items-center gap-1"><WarningCircle size={14} weight="fill" /> Day not closed</span>
                    : r.past_midnight ? <span className="text-[11px] font-bold text-amber-700 flex items-center gap-1"><MoonStars size={14} weight="fill" /> Past midnight</span>
                      : <span className="text-[11px] font-semibold text-slate-400">{r.operating_dates[0] ? fmtDay(r.operating_dates[0]) : "—"}</span>}
                </td>
                <td className="px-3 py-3 text-xs text-slate-500">{r.last_activity ? fmtTime(r.last_activity) : "—"}</td>
              </tr>
            ))}
            {data && rows.length === 0 && <tr><td colSpan={11} className="px-6 py-10 text-center text-slate-400">No outlets match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {exporting && <ExportDialog venues={hier?.venues || []} defaultVenueId={f.venue_id} defaultDeptId={f.department_id} onClose={() => setExporting(false)} />}
    </div>
  );
}
